import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { IonicModule, ToastController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, switchMap, take } from 'rxjs/operators';

import { UserService } from '../global/userService/user-service';
import { NotificationService } from './notification.service';
import { UserNotification } from './models';
import { RegistrationDetailsFormComponent } from './registration-details-form.component';
import { Modal } from '../global/modal';
import { ModalModule } from '../global/modal/modal.module';

/**
 * Place this component once in the app shell (e.g. alongside the router-outlet).
 * It checks for unread co-organiser notifications on login and prompts accordingly.
 */
@Component({
    standalone: true,
    selector: 'abs-co-organiser-notification-handler',
    imports: [IonicModule, TranslateModule, RegistrationDetailsFormComponent, ModalModule],
    template: `
        <ng-template #displayNameForm>
            <p>{{ displayNameMessage }}</p>
            <abs-registration-details-form
                [name]="displayNameValue"
                [showContactEmail]="false"
                (nameChange)="displayNameValue = $event"
                (validChange)="displayNameValid = $event"
            ></abs-registration-details-form>
        </ng-template>
        <ng-template #displayNameButtons>
            <ion-button [disabled]="!displayNameValid" (click)="saveDisplayName()">
                {{ 'GLOBAL.SAVE' | translate }}
            </ion-button>
        </ng-template>
    `
})
export class CoOrganiserNotificationHandlerComponent implements OnInit {
    private notificationService = inject(NotificationService);
    private userService = inject(UserService);
    private toastCtrl = inject(ToastController);
    private translate = inject(TranslateService);
    private modal = inject(Modal);

    @ViewChild('displayNameForm') displayNameForm: TemplateRef<any>;
    @ViewChild('displayNameButtons') displayNameButtons: TemplateRef<any>;

    displayNameValue = '';
    displayNameValid = false;
    displayNameMessage = '';
    private pendingNotifications: UserNotification[] = [];

    ngOnInit() {
        // Wait until the user is logged in, then check notifications
        this.userService.loggedIn$
            .pipe(
                filter(Boolean),
                take(1),
                switchMap(() => this.notificationService.getNotifications())
            )
            .subscribe((notifications) => {
                const unread = notifications.filter((n) => !n.read && n.type === 'co_organiser_added');
                if (unread.length === 0) return;

                const hasDisplayName = !!this.userService.user()?.displayName;

                if (!hasDisplayName) {
                    this.promptSetDisplayName(unread);
                } else {
                    this.showToast(unread);
                }
            });
    }

    private promptSetDisplayName(notifications: UserNotification[]) {
        this.pendingNotifications = notifications;
        const eventTitles = notifications.map((n) => n.eventTitle).join(', ');
        this.displayNameMessage = this.translate.instant('GLOBAL.EVENTS.NOTIFICATIONS.SET_DISPLAY_NAME_MESSAGE', { eventTitles });
        this.displayNameValue = '';
        this.displayNameValid = false;
        this.modal.show({
            props: {
                // This component's own existing title key, not event-create's.
                title: this.translate.instant('GLOBAL.EVENTS.NOTIFICATIONS.CO_ORG_ADDED_HEADER'),
                contentTemplate: this.displayNameForm,
                buttonsTemplate: this.displayNameButtons
            }
        });
    }

    saveDisplayName() {
        const name = this.displayNameValue.trim();
        if (!name) {
            return;
        }
        const notifications = this.pendingNotifications;
        this.userService.updateDisplayName(name).subscribe(() => {
            this.userService.refreshAccessToken();
            this.markAllRead(notifications);
        });
    }

    private async showToast(notifications: UserNotification[]) {
        const eventTitles = notifications.map((n) => n.eventTitle).join(', ');

        const toast = await this.toastCtrl.create({
            message: this.translate.instant('GLOBAL.EVENTS.NOTIFICATIONS.CO_ORG_ADDED_TOAST', { eventTitles }),
            duration: 4000,
            position: 'top',
            color: 'primary'
        });
        await toast.present();

        this.markAllRead(notifications);
    }

    private markAllRead(notifications: UserNotification[]) {
        notifications.forEach((n) => {
            this.notificationService.markRead(n.id).subscribe();
        });
    }
}
